import { DomainSchema } from '@multitec/domain'
import { useQuery } from 'react-query'
import { z } from 'zod'

import { Base } from '../base'
import { MyURLSearchParams } from '../utils/url-search-params'

const BASE_URL = '/entity/storages'

class Search extends Base {
  private schema = z.object({
    storage_id: z.string().optional(),
    order_id: z.number().optional(),
    origin_order_id: z.number().optional(),
    target_order_id: z.number().optional(),
    item_id: z.string().optional(),
    date_start: z.string().optional(),
    date_end: z.string().optional(),
  })

  private response = z.array(DomainSchema.StorageTransaction)

  handle = ({ storage_id, ...params }: z.infer<typeof this.schema>) => {
    return useQuery(
      ['storage-transaction', 'search', { storage_id, ...params }],
      async () => {
        if (!storage_id) return []

        let queryParams = ''
        if (params) queryParams = new MyURLSearchParams(params).toString()

        const url = `${BASE_URL}/${storage_id}/transactions?${queryParams}`
        const { data } = await this.axios.get(url)

        return this.response.parse(data.result)
      },
    )
  }
}

class CreateSupply extends Base {
  private body = z.object({
    storage_id: z.string(),
    description: z.string().nullable().optional(),
    user_id: z.string(),
    items: z.array(
      z.object({
        item_id: z.string(),
        quantity: z.number(),
      }),
    ),
  })

  private response = z.array(DomainSchema.StorageTransaction)

  handle = async ({ storage_id, ...body }: z.infer<typeof this.body>) => {
    const { data } = await this.axios.post(
      BASE_URL + `/${storage_id}/transactions/supply`,
      body,
    )
    return this.response.parse(data.result)
  }
}

class CreateTransfer extends Base {
  private body = z.object({
    storage_id: z.string(),
    target_storage_id: z.string(),
    description: z.string().nullable().optional(),
    user_id: z.string(),
    items: z.array(
      z.object({
        item_id: z.string(),
        quantity: z.number(),
      }),
    ),
  })

  private response = z.array(DomainSchema.StorageTransaction)

  handle = async ({ storage_id, ...body }: z.infer<typeof this.body>) => {
    const { data } = await this.axios.post(
      BASE_URL + `/${storage_id}/transactions/transfer`,
      body,
    )
    return this.response.parse(data.result)
  }
}

class CreateCheckout extends Base {
  private body = z.object({
    storage_id: z.string(),
    target_order_id: z.number().optional(),
    description: z.string().nullable().optional(),
    user_id: z.string(),
    items: z.array(
      z.object({
        item_id: z.string(),
        quantity: z.number(),
      }),
    ),
  })

  private response = z.array(DomainSchema.StorageTransaction)

  handle = async ({ storage_id, ...body }: z.infer<typeof this.body>) => {
    const { data } = await this.axios.post(
      BASE_URL + `/${storage_id}/transactions/checkout`,
      body,
    )
    return this.response.parse(data.result)
  }
}

class CreateCheckIn extends Base {
  private body = z.object({
    storage_id: z.string(),
    origin_order_id: z.number(),
    description: z.string().nullable().optional(),
    user_id: z.string(),
    items: z.array(
      z.object({
        item_id: z.string(),
        quantity: z.number(),
      }),
    ),
  })

  private response = z.array(DomainSchema.StorageTransaction)

  handle = async ({ storage_id, ...body }: z.infer<typeof this.body>) => {
    const { data } = await this.axios.post(
      BASE_URL + `/${storage_id}/transactions/checkin`,
      body,
    )
    return this.response.parse(data.result)
  }
}

export default class extends Base {
  Search = new Search(this.axios).handle
  CreateSupply = new CreateSupply(this.axios).handle
  CreateTransfer = new CreateTransfer(this.axios).handle
  CreateCheckIn = new CreateCheckIn(this.axios).handle
  CreateCheckout = new CreateCheckout(this.axios).handle
}
